import React, { useState } from 'react';
import { useNavigate, Link, useSearch } from '@tanstack/react-router';
import SessionJoiner from '../../components/student/SessionJoiner';
import { Button } from '../../components/ui/button';
import { api, useMutation } from '../../lib/convex';
import { useConvex } from 'convex/react';

// Define the search parameters type
interface JoinSearchParams {
  code?: string;
}

const JoinSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const convex = useConvex();
  const search = useSearch({ from: '/join' }) as JoinSearchParams;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  
  const joinSession = useMutation(api.participants.join);

  const handleJoinSession = async (sessionCode: string, nickname: string) => {
    setIsLoading(true);
    setError(undefined);
    
    try {
      const sessionData = await convex.query(api.sessions.getByCode, { code: sessionCode });
      
      if (!sessionData) {
        setError('Invalid session code. Please check and try again.');
        setIsLoading(false);
        return;
      }
      
      if (sessionData.status === 'completed') {
        setError('This session has ended.');
        setIsLoading(false);
        return;
      }
      
      const participantId = await joinSession({
        sessionId: sessionData._id,
        nickname,
      });
      
      // Store in localStorage for recovery
      localStorage.setItem(`session_${sessionData._id}`, JSON.stringify({
        participantId,
        nickname,
        sessionId: sessionData._id,
      }));
      
      navigate({ 
        to: `/quiz/${sessionData._id}`, 
        search: { nickname, participantId } 
      });
      
    } catch (error) {
      console.error('Error joining session:', error);
      setError('Failed to join session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/">
          <Button variant="outline" size="sm">
            ← Back to Home
          </Button>
        </Link>
      </div>
      
      <SessionJoiner 
        onJoin={handleJoinSession} 
        isLoading={isLoading}
        error={error}
        initialCode={search.code}
      />
    </div>
  );
};

export default JoinSessionPage;